/**
 * THE INTERACTIVE TRADE SIMULATOR'S EXECUTION MODEL — one trade, from a signal bar.
 *
 * <h2>Two different claims sit on one card, and they must not be confused</h2>
 *
 * <pre>
 *   the ODDS     an ESTIMATE made at bar t from bars 0..t-1      see ctsOddsEngine.js
 *   the RESULT   an OUTCOME measured over bars t+1..t+horizon    this file
 * </pre>
 *
 * <p>The result necessarily reads bars after the signal — that is what a result IS — and the
 * odds necessarily do not. Stating it here because a card showing "83% odds" beside a LOSS
 * invites exactly the wrong reading, that the estimate had seen the outcome. The engine keeps
 * them in separate functions and the dialog labels them, and this is the only place that
 * touches the future at all.
 *
 * <h2>Provider-agnostic by construction</h2>
 *
 * Nothing here knows what a CTS point is. It takes a bar index, a direction and a RULE SET, so
 * plugging PTA or PTB in later is supplying different rules — which for those two will be
 * geometric (zones, band heights) rather than volatility-scaled. The one thing a new provider
 * must not have to re-implement is the fill model below, which is where the real decisions are.
 */

import { BAR_OPEN, BAR_CLOSE, BAR_LOW, BAR_HIGH } from './analyticaGeometry';
import {
  EMPTY_EXCURSIONS, excursionsOver, tradeTelemetry,
} from './tradeSimMetrics';

/** Wilder's own period, and the one every other ATR in this repository uses. */
export const ATR_PERIOD = 14;

const n = (v) => (typeof v === 'number' && Number.isFinite(v) ? v : null);

/**
 * Wilder-smoothed ATR, as a series.
 *
 * <p>WILDER, not a simple mean of the last N true ranges. The Java side smooths over the whole
 * series and a simple mean here would put the browser's stop in a different place from the
 * engine's for the same bar — two answers to one question, which is the defect this repository
 * keeps recording. Null until the period has elapsed, rather than a partial average: a stop
 * priced off three bars is not a volatility stop, and a rule that silently used one would be
 * tightest exactly at the start of every chart.
 */
export function atrSeries(ohlc, period = ATR_PERIOD) {
  const bars = Array.isArray(ohlc) ? ohlc : [];
  const out = new Array(bars.length).fill(null);
  const p = Math.max(1, Math.floor(n(period) || ATR_PERIOD));
  let sum = 0;
  let prev = null;
  for (let i = 0; i < bars.length; i++) {
    const high = n(bars[i]?.[BAR_HIGH]);
    const low = n(bars[i]?.[BAR_LOW]);
    const close = n(bars[i]?.[BAR_CLOSE]);
    if (high === null || low === null || close === null) { prev = null; continue; }
    const prevClose = i > 0 ? n(bars[i - 1]?.[BAR_CLOSE]) : null;
    const tr = prevClose === null
      ? high - low
      : Math.max(high - low, Math.abs(high - prevClose), Math.abs(low - prevClose));
    if (i < p) {
      sum += tr;
      if (i === p - 1) { prev = sum / p; out[i] = prev; }
      continue;
    }
    prev = prev === null ? tr : ((prev * (p - 1)) + tr) / p;
    out[i] = prev;
  }
  return out;
}

/** The three ways a simulated trade can end, plus the one way it can fail to. */
export const OUTCOMES = Object.freeze({
  WIN: 'WIN',
  LOSS: 'LOSS',
  NEUTRAL: 'NEUTRAL',
  OPEN: 'OPEN',
});

export const EXIT_REASONS = Object.freeze({
  TARGET: 'target reached',
  STOP: 'stop hit',
  HORIZON: 'horizon expired',
  UNRESOLVED: 'not yet resolved',
});

/**
 * One simulated trade.
 *
 * <h2>The fill model, which is where the decisions are</h2>
 *
 * <ul>
 *   <li><b>Entry is the signal bar's own CLOSE.</b> The direction is decided from that close, so
 *       it is the first price at which the decision existed. Filling at the bar's open would be
 *       trading on the signal before the bar that produced it had printed.</li>
 *   <li><b>A GAP fills at the OPEN, not at the level.</b> A bar that opens beyond the target
 *       never traded at it, so booking the level would hand the strategy the best price of every
 *       gap day — and a signal-driven rule concentrates its exits in exactly the bars that gap.
 *       The same rule applies to the stop, in the operator's disfavour.</li>
 *   <li><b>A bar reaching BOTH the target and the stop books the STOP.</b> Daily data cannot say
 *       which was touched first, so the adverse one is honoured. This is the convention every
 *       other gap rule in this repository already follows, and it is the only one that cannot
 *       flatter the result.</li>
 *   <li><b>No ATR, no trade.</b> Refused rather than priced from a guessed stop distance, so a
 *       card never reports a result whose risk nobody can state. The first 14 bars of a chart.</li>
 * </ul>
 *
 * <h2>OPEN is not NEUTRAL</h2>
 *
 * A signal whose horizon runs past the last bar has no outcome yet, and calling that a neutral
 * expiry would make every recent signal look flat for the sole reason that time has not passed.
 * It carries a mark-to-market so the card can still say something, and the session ledger
 * excludes it from every rate — a mark is not a result.
 *
 * @param {object} input
 * @param {Array<number[]>} input.ohlc  ECharts order: [open, close, low, high]
 * @param {number} input.index          the signal bar
 * @param {'up'|'down'} input.direction
 * @param {{targetAtr:number, stopAtr:number, horizon:number}} input.rules
 * @param {Array<number|null>} [input.atr] precomputed, so a chart does not recompute per click
 * @param {number} [input.maxIndex] THE REPLAY BOUNDARY — the last bar the reader can see. The
 *        walk stops there and the trade reports OPEN, so a replay cannot price a result from
 *        bars that are off the end of the drawn chart.
 */
export function simulateTrade({
  ohlc = [], index, direction = 'up', rules = {}, atr = null, maxIndex = null,
} = {}) {
  const bars = Array.isArray(ohlc) ? ohlc : [];
  const i = Math.floor(n(index) ?? -1);
  const entry = n(bars[i]?.[BAR_CLOSE]);
  if (entry === null) return null;

  const sign = direction === 'down' ? -1 : 1;
  const horizon = Math.max(1, Math.floor(n(rules.horizon) || 10));
  const targetAtr = Math.max(0, n(rules.targetAtr) ?? 1);
  const stopAtr = Math.max(0, n(rules.stopAtr) ?? 1);
  const series = Array.isArray(atr) ? atr : atrSeries(bars);
  const vol = n(series[i]);

  const base = {
    index: i, direction, entryPrice: entry, horizon, targetAtr, stopAtr, atr: vol,
  };
  if (vol === null || vol <= 0) {
    return {
      ...base,
      target: null, stop: null, risk: null,
      outcome: null, exitReason: 'no ATR at this bar — the trade cannot be priced',
      exitIndex: null, exitPrice: null, barsHeld: null,
      pnl: null, pnlPercent: null, rMultiple: null, priced: false,
      // Declared as nulls rather than omitted: an absent key and a null one read identically
      // at a call site and only one of them is a promise about the shape.
      ...EMPTY_EXCURSIONS, holdReturnPercent: null, alphaPercent: null,
    };
  }

  const target = entry + (sign * targetAtr * vol);
  const stop = entry - (sign * stopAtr * vol);
  const risk = Math.abs(entry - stop);
  // THE SMALLER OF the series and the boundary. One `last` rather than a second branch, so
  // every path below — the walk, the OPEN case, the mark-to-market — is bounded by it without
  // anybody having to remember a replay exists.
  const bound = Number.isFinite(maxIndex) ? Math.floor(maxIndex) : bars.length - 1;
  const last = Math.max(-1, Math.min(bars.length - 1, bound));
  const horizonIndex = i + horizon;

  const book = (exitIndex, exitPrice, outcome, exitReason) => {
    const move = (exitPrice - entry) * sign;
    // SIGNED BY DIRECTION, so a short that fell is positive. `pnlPercent` is the field
    // calculatePortfolioMetrics reads, and an unsigned one would score every winning short
    // as a loss.
    const pnlPercent = (move / entry) * 100;
    return {
      ...base,
      target,
      stop,
      risk,
      outcome,
      exitReason,
      exitIndex,
      exitPrice,
      barsHeld: exitIndex - i,
      pnl: move,
      pnlPercent,
      rMultiple: risk > 0 ? move / risk : null,
      /*
       * THE EXECUTION TELEMETRY, over the bars the position was actually held.
       *
       * Computed from the SAME `exitIndex` the P&L is booked at, so MAE can never cover a bar
       * the trade was not in and the benchmark can never span a window the trade did not hold.
       * In a replay `exitIndex` is already bounded by `last`, so this inherits the boundary
       * rather than having to re-apply it.
       */
      ...tradeTelemetry({
        ohlc: bars, entryIndex: i, exitIndex, entryPrice: entry, sign, risk, pnlPercent,
      }),
      priced: true,
    };
  };

  for (let k = i + 1; k <= Math.min(horizonIndex, last); k++) {
    const open = n(bars[k]?.[BAR_OPEN]);
    const high = n(bars[k]?.[BAR_HIGH]);
    const low = n(bars[k]?.[BAR_LOW]);
    if (open === null || high === null || low === null) continue;

    const hitTarget = sign > 0 ? high >= target : low <= target;
    const hitStop = sign > 0 ? low <= stop : high >= stop;

    // THE STOP IS TESTED FIRST, so a bar reaching both books the adverse one.
    if (hitStop) {
      const gapped = sign > 0 ? open <= stop : open >= stop;
      return book(k, gapped ? open : stop, OUTCOMES.LOSS, EXIT_REASONS.STOP);
    }
    if (hitTarget) {
      const gapped = sign > 0 ? open >= target : open <= target;
      return book(k, gapped ? open : target, OUTCOMES.WIN, EXIT_REASONS.TARGET);
    }
  }

  if (horizonIndex > last) {
    const mark = n(bars[last]?.[BAR_CLOSE]);
    const move = mark === null ? null : (mark - entry) * sign;
    return {
      ...base,
      target,
      stop,
      risk,
      outcome: OUTCOMES.OPEN,
      exitReason: EXIT_REASONS.UNRESOLVED,
      exitIndex: null,
      exitPrice: null,
      barsHeld: last - i,
      // A MARK, named as one. Carried on its own fields so nothing can fold it into a realised
      // tally by reading `pnlPercent` off an OPEN trade.
      markPrice: mark,
      markPnl: move,
      markPnlPercent: move === null ? null : (move / entry) * 100,
      /*
       * THE EXCURSIONS SO FAR, which an open position genuinely has.
       *
       * How far it has gone against you is a fact about the bars already drawn, so it is
       * reportable while the outcome is not — and in a replay it is the number a reader
       * stepping a trade out is watching. ALPHA is deliberately absent: for a long marked at
       * the last revealed close it is identically zero by construction, which would put a
       * figure on the card that cannot say anything.
       */
      ...excursionsOver(bars, i, last, entry, sign, risk),
      holdReturnPercent: null,
      alphaPercent: null,
      pnl: null,
      pnlPercent: null,
      rMultiple: null,
      priced: true,
    };
  }

  const close = n(bars[horizonIndex]?.[BAR_CLOSE]);
  if (close === null) return null;
  return book(horizonIndex, close, OUTCOMES.NEUTRAL, EXIT_REASONS.HORIZON);
}

export default simulateTrade;
